// src/layouts/AdminProtectedLayout.jsx
import React, { useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../hooks/useAppDispatch.js'
import { validateToken, initializeAuth } from '../redux/slices/authSlice.js'
import AdminLayout from './AdminLayout.jsx'

export default function AdminProtectedLayout() {
    const location = useLocation()
    const dispatch = useAppDispatch()
    const { isAuthenticated, user, loading, initialized } = useAppSelector((state) => state.auth)
    const [authChecked, setAuthChecked] = useState(false)

    // Initialize auth on mount
    useEffect(() => {
        dispatch(initializeAuth())
    }, [dispatch])

    // Validate token on mount and when location changes
    useEffect(() => {
        const checkAuth = async () => {
            const token = localStorage.getItem('token')
            const userStr = localStorage.getItem('user')

            // If no token or user data, not authenticated
            if (!token || !userStr) {
                setAuthChecked(true)
                return
            }

            // If we have token but state isn't initialized, validate it
            if (!initialized) {
                try {
                    await dispatch(validateToken()).unwrap()
                } catch (error) {
                    // Token validation failed, auth state will be cleared by reducer
                    console.error('Token validation failed:', error)
                }
            }

            setAuthChecked(true)
        }

        checkAuth()
    }, [dispatch, initialized, location.pathname])

    // Show loading during initial auth check
    if (!authChecked || (initialized && loading)) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading...</p>
                </div>
            </div>
        )
    }

    // If not authenticated, redirect to login
    if (!isAuthenticated) {
        return <Navigate to="/admin/login" state={{ from: location }} replace />
    }

    // If authenticated but not admin, redirect to login
    if (user?.role !== 'admin') {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        return <Navigate to="/admin/login" state={{ from: location }} replace />
    }

    // User is authenticated and is admin
    return (
        <AdminLayout>
            <Outlet />
        </AdminLayout>
    )
}